import { useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useUpdateProfile } from '@/hooks/useUpdateProfile'
import { usePuroksForBarangayPsgc } from '@/hooks/usePsgcCascade'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Save, Loader2, MapPin } from 'lucide-react'

export function AddressPage() {
  const { profile } = useAuth()
  const update = useUpdateProfile()
  const { data: puroks } = usePuroksForBarangayPsgc(profile?.barangay?.psgc_code ?? null)
  const hasPuroks = puroks && puroks.length > 0

  const [purokId, setPurokId] = useState(profile?.purok_id ?? '')
  const [houseNumber, setHouseNumber] = useState(profile?.house_number ?? '')
  const [streetName, setStreetName] = useState(profile?.street_name ?? '')
  const [addressLine, setAddressLine] = useState(profile?.address_line ?? '')

  if (!profile) return null

  const handleSave = () => {
    update.mutate({
      purok_id: purokId || null,
      house_number: houseNumber || null,
      street_name: streetName || null,
      address_line: addressLine || null,
    })
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="text-base">Barangay (read-only)</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {profile.barangay?.name ?? 'Unknown'} — To change your barangay, contact your admin.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Address Details</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {hasPuroks && (
            <div className="space-y-1">
              <Label>Purok</Label>
              <Select value={purokId} onValueChange={setPurokId}>
                <SelectTrigger><SelectValue placeholder="Select purok" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {puroks!.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>House Number</Label><Input value={houseNumber} onChange={(e) => setHouseNumber(e.target.value)} /></div>
            <div className="space-y-1"><Label>Street Name</Label><Input value={streetName} onChange={(e) => setStreetName(e.target.value)} /></div>
          </div>
          <div className="space-y-1">
            <Label>Sitio / Landmark</Label>
            <textarea className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" value={addressLine} onChange={(e) => setAddressLine(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex items-center gap-3 py-8 text-muted-foreground">
          <MapPin className="h-8 w-8" />
          <p className="text-sm">Map view coming soon</p>
        </CardContent>
      </Card>

      <Button className="w-full" onClick={handleSave} disabled={update.isPending}>
        {update.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        Save Changes
      </Button>
    </div>
  )
}
